// ============================================
// Credits Overlay
// Triggered from main menu footer button.
// ============================================

import { L } from '../systems/localization.js';

// Curated contributor buckets. Derived from foundby: tags in changelog.js
// with alias-merging (synaps97/Synaps97/synaps → synaps97 etc.) and
// trash-filter (the "Community" placeholder isn't a person). Hand-curated
// because the changelog tags are inconsistently cased and the bucket
// thresholds reflect editorial judgement, not raw counts.
const CONTRIBUTORS = {
    hallOfFame: [
        'Anne-Sophie',
        'synaps97',
    ],
    major: [
        'The Cook',
        'FlatMarsSociety',
        'Jürgen',
        'BlueOwl',
        'AKinkyMutt',
        'Sasha',
        'Pea',
        'iluan',
        'Selaiko',
        'Marcus',
        'umbra-kiarara',
    ],
    hunters: [
        '0zz1e', 'adyyxy', 'Amanda', 'almostamanda', 'Batl', 'bobkelpie7',
        'Brendo', 'dairine_fmnzd', 'dthrcum', 'eturtlewitness', 'Ewokj',
        'Jay', 'Joe', 'kaneeden', 'kara', 'Kirara', 'Lee', 'Lenz',
        'mcats', 'Niya', 'Pablito', 'PartyRav3', 'ScarletMemory',
        'Shooter84', 'Sinstrall', 'splattertang', 'Tovarisch_Hvostik',
        'Virtualmagic', 'WolfsMane', 'Yuno', 'Zackie',
    ],
};

class CreditsUI {
    constructor() {
        this._built = false;
        this._overlay = null;
    }

    open() {
        if (!this._built) this._build();
        this._overlay.classList.remove('hidden');
        this._refreshL10n();
        document.addEventListener('keydown', this._escHandler);
    }

    close = () => {
        if (!this._overlay) return;
        this._overlay.classList.add('hidden');
        document.removeEventListener('keydown', this._escHandler);
    };

    _escHandler = (e) => {
        if (e.key === 'Escape') this.close();
    };

    _build() {
        const overlay = document.createElement('div');
        overlay.id = 'credits-overlay';
        overlay.className = 'overlay credits-overlay hidden';
        overlay.innerHTML = `
            <div class="overlay-box credits-box">
                <button type="button" class="credits-close" aria-label="Close">&times;</button>

                <header class="credits-header">
                    <h2 class="credits-title" data-l10n="credits.title">Credits</h2>
                    <p class="credits-subtitle" data-l10n="credits.subtitle">Made possible by an incredible community</p>
                </header>

                <section class="credits-section credits-special">
                    <h3 class="credits-section-title" data-l10n="credits.special.title">Special Thanks</h3>

                    <div class="credits-card credits-card-art">
                        <div class="credits-card-names">Sasha &amp; ImBlueOwl</div>
                        <div class="credits-card-role" data-l10n="credits.special.art_role">AI &amp; Image Generation Support</div>
                        <div class="credits-card-blurb" data-l10n="credits.special.art_blurb">Endless prompt iteration, LoRA training, and the visual identity behind every scene</div>
                    </div>

                    <div class="credits-card credits-card-translate">
                        <div class="credits-card-names">Marcus</div>
                        <div class="credits-card-role" data-l10n="credits.special.de_role">German Translation</div>
                        <div class="credits-card-blurb" data-l10n="credits.special.de_blurb">Thousands of lines carried into German with care</div>
                    </div>
                </section>

                <section class="credits-section credits-hunters">
                    <h3 class="credits-section-title" data-l10n="credits.hunters.title">Bug Hunters</h3>
                    <p class="credits-section-sub" data-l10n="credits.hunters.sub">Players who shared bugs, saves, and screenshots. Every report shaped a fix</p>

                    <div class="credits-bucket">
                        <h4 class="credits-bucket-title" data-l10n="credits.hunters.hof">Hall of Fame</h4>
                        <div class="credits-chips" data-bucket="hallOfFame"></div>
                    </div>

                    <div class="credits-bucket">
                        <h4 class="credits-bucket-title" data-l10n="credits.hunters.major">Major Contributors</h4>
                        <div class="credits-chips" data-bucket="major"></div>
                    </div>

                    <div class="credits-bucket">
                        <h4 class="credits-bucket-title" data-l10n="credits.hunters.all">Bug Hunters</h4>
                        <div class="credits-chips" data-bucket="hunters"></div>
                    </div>
                </section>

                <section class="credits-section credits-community">
                    <h3 class="credits-section-title" data-l10n="credits.community.title">To the Community</h3>
                    <p class="credits-community-text" data-l10n="credits.community.patrons">To every Patron whose support keeps this project alive</p>
                    <p class="credits-community-text" data-l10n="credits.community.players">To every player who picked up the game, finished a run, or sent feedback</p>

                    <div class="credits-cta-row">
                        <a href="https://www.patreon.com/PinkedAndOwned" target="_blank" rel="noopener" class="credits-cta credits-cta-patreon">
                            <span data-l10n="credits.cta.patreon">Support on Patreon</span>
                        </a>
                        <a href="https://discord.gg/2wFGg3nWhJ" target="_blank" rel="noopener" class="credits-cta credits-cta-discord">
                            <span data-l10n="credits.cta.discord">Join the Discord</span>
                        </a>
                    </div>
                </section>

                <footer class="credits-footer">
                    <p class="credits-thanks" data-l10n="credits.thanks">Thank you for playing &#9829;</p>
                </footer>
            </div>
        `;
        document.body.appendChild(overlay);
        this._overlay = overlay;
        this._built = true;

        for (const [bucket, names] of Object.entries(CONTRIBUTORS)) {
            const container = overlay.querySelector(`[data-bucket="${bucket}"]`);
            if (!container) continue;
            container.innerHTML = names.map(n =>
                `<span class="credits-chip">${this._escape(n)}</span>`
            ).join('');
        }

        overlay.querySelector('.credits-close').addEventListener('click', this.close);
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) this.close();
        });
    }

    _refreshL10n() {
        if (!this._overlay) return;
        for (const el of this._overlay.querySelectorAll('[data-l10n]')) {
            const key = el.getAttribute('data-l10n');
            const txt = L.get(key);
            if (txt && txt !== key) el.textContent = txt;
        }
    }

    _escape(s) {
        return String(s).replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
    }
}

export const creditsUI = new CreditsUI();
